import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, ArrowRight, AlertCircle, Check, RefreshCw, Eye, EyeOff, Lock } from 'lucide-react';
import BackButton from './BackButton';
import './Auth.css';

const API_URL = `${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/auth`;

export default function ForgotPassword() {
  const [step, setStep] = useState(1); // 1: email, 2: OTP, 3: new password
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const navigate = useNavigate();

  // Password requirements
  const requirements = [
    { met: newPassword.length >= 8, text: 'At least 8 characters' },
    { met: /[A-Z]/.test(newPassword), text: 'One uppercase letter' },
    { met: /[a-z]/.test(newPassword), text: 'One lowercase letter' },
    { met: /[0-9]/.test(newPassword), text: 'One number' },
  ];

  const allRequirementsMet = requirements.every(r => r.met);

  // Start cooldown timer for resend
  const startResendCooldown = () => {
    setResendCooldown(60);
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Step 1: Send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/forgot-password/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to send reset code');
      }

      setStep(2);
      startResendCooldown();
      setSuccess('If an account exists with this email, you will receive a reset code.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (otp.length !== 6) {
      setError('Please enter the 6-digit code');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/forgot-password/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to verify code');
      }

      setResetToken(data.resetToken);
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!allRequirementsMet) {
      setError('Please meet all password requirements');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/forgot-password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resetToken, newPassword }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      setSuccess('Password reset successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/forgot-password/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to resend code');
      }

      startResendCooldown();
      setSuccess('New code sent to your email');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Left Side - Branding */}
      <div className="login-branding">
        <div className="login-branding-content">
          <BackButton />
          <Link to="/" className="login-logo">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="50" height="50" className="logo-img">
              <rect width="32" height="32" fill="var(--bg-tertiary)"></rect>
              <rect x="6" y="4" width="16" height="5" fill="var(--accent-primary)"></rect>
              <rect x="6" y="4" width="5" height="8" fill="var(--accent-primary)"></rect>
              <rect x="6" y="12" width="16" height="5" fill="var(--accent-primary)"></rect>
              <rect x="17" y="17" width="5" height="7" fill="var(--accent-primary)"></rect>
              <rect x="6" y="23" width="16" height="5" fill="var(--accent-primary)"></rect>
              <polygon points="22,4 26,7 26,12 22,9" fill="#1a1a2e"></polygon>
              <polygon points="22,12 26,15 26,20 22,17" fill="#1a1a2e"></polygon>
              <polygon points="22,23 26,26 26,31 22,28" fill="#1a1a2e"></polygon>
              <polygon points="6,28 22,28 26,31 10,31" fill="#1a1a2e"></polygon>
            </svg>
            <span className="logo-text">SCRAWL</span>
          </Link>
          
          <h1 className="login-headline">
            Forgot your<br />
            <span className="highlight">password?</span>
          </h1>
          
          <p className="login-description">
            No worries! Enter your email and we'll send you a code to reset your password.
          </p>

          <div className="login-features">
            <div className="login-feature-item">
              <span className="login-feature-text">1. Enter your email address</span>
            </div>
            <div className="login-feature-item">
              <span className="login-feature-text">2. Check your inbox for the code</span>
            </div>
            <div className="login-feature-item">
              <span className="login-feature-text">3. Create a new password</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="login-form-side">
        <div className="login-form-container">
          {step === 1 && (
            <>
              <div className="login-form-header">
                <h2>Reset Password</h2>
                <p>Enter your email to receive a reset code</p>
              </div>

              <form onSubmit={handleSendOTP} className="login-form">
                {error && (
                  <div className="login-error">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                  </div>
                )}

                <div className="login-field">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    autoFocus
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || !email}
                  className="login-submit"
                >
                  {loading ? (
                    <span>Sending code...</span>
                  ) : (
                    <>
                      <Mail size={20} />
                      Send Reset Code
                    </>
                  )}
                </button>
              </form>

              <div className="login-footer">
                <span>Remember your password?</span>
                <Link to="/login" className="login-link">
                  Sign in <ArrowRight size={16} />
                </Link>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="login-form-header">
                <h2>Enter Code</h2>
                <p>We sent a code to <strong>{email}</strong></p>
              </div>

              <form onSubmit={handleVerifyOTP} className="login-form">
                {error && (
                  <div className="login-error">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                  </div>
                )}

                {success && (
                  <div className="login-success">
                    <Check size={18} />
                    <span>{success}</span>
                  </div>
                )}

                <div className="login-field">
                  <label>Verification Code</label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    required
                    autoFocus
                    maxLength={6}
                    className="otp-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  className="login-submit"
                >
                  {loading ? (
                    <span>Verifying...</span>
                  ) : (
                    <>
                      <Check size={20} />
                      Verify Code
                    </>
                  )}
                </button>

                <div className="otp-actions">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={resendCooldown > 0 || loading}
                    className="resend-btn"
                  >
                    <RefreshCw size={16} />
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setStep(1); setOtp(''); setError(''); setSuccess(''); }}
                    className="back-to-form-btn"
                  >
                    Change Email
                  </button>
                </div>
              </form>
            </>
          )}

          {step === 3 && (
            <>
              <div className="login-form-header">
                <h2>New Password</h2>
                <p>Create a strong new password</p>
              </div>

              <form onSubmit={handleResetPassword} className="login-form">
                {error && (
                  <div className="login-error">
                    <AlertCircle size={18} />
                    <span>{error}</span>
                  </div>
                )}

                {success && (
                  <div className="login-success">
                    <Check size={18} />
                    <span>{success}</span>
                  </div>
                )}

                <div className="login-field">
                  <label>New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      required
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="password-toggle"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>

                  {newPassword && (
                    <div className="password-requirements">
                      {requirements.map((req, i) => (
                        <div key={i} className={`requirement ${req.met ? 'met' : ''}`}>
                          {req.met ? <Check size={12} /> : <span className="requirement-dot"></span>}
                          <span>{req.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="login-field">
                  <label>Confirm Password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    className={confirmPassword && newPassword !== confirmPassword ? 'input-error' : ''}
                  />
                  {confirmPassword && newPassword !== confirmPassword && (
                    <span className="field-error">Passwords don't match</span>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !allRequirementsMet || newPassword !== confirmPassword}
                  className="login-submit"
                >
                  {loading ? (
                    <span>Resetting...</span>
                  ) : (
                    <>
                      <Lock size={20} />
                      Reset Password
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
